import { JourneyDayItem, JourneyDayTripItem, TripOptionItem } from "../../models/axiosModel"
import { JourneyOptionsByDateValue } from "../../context/quotationContext"
import { toTitleCase } from "util/textcase"
import { tripKindFromDayTripType } from "./journeyDetailsOptionsPerformanceUtil"

export type TripSelectOption = { label: string; value: string; disabled?: boolean }

/** `0` / `"0"` / blank are not real source cities — treating them as present builds `0::id` values that never match options. */
export const isPresentSourceCityId = (raw: unknown): boolean => {
  if (raw == null || raw === "") return false
  const text = String(raw).trim()
  if (!text || text === "0") return false
  const numeric = Number(text)
  if (!Number.isNaN(numeric) && numeric === 0) return false
  return true
}

type SourceCityCarrier = { _source_city_id?: unknown; city_id?: unknown }

export const resolveTripSourceCityIdString = (
  item: unknown,
  fallbackCityId?: unknown,
): string => {
  const fields = (item ?? {}) as SourceCityCarrier
  const candidates = [fields._source_city_id, fields.city_id, fallbackCityId]
  for (const candidate of candidates) {
    if (isPresentSourceCityId(candidate)) return String(candidate)
  }
  return ""
}

export const buildTripSelectValue = (
  trip: Pick<JourneyDayTripItem, "day_trip_id"> & { _source_city_id?: unknown; city_id?: unknown },
  fallbackCityId?: unknown,
): string => {
  const tripId = String(trip?.day_trip_id ?? "").trim()
  if (!tripId) return ""
  const source = resolveTripSourceCityIdString(trip, fallbackCityId)
  return source ? `${source}::${tripId}` : tripId
}

/** Keep the closed Select label visible even when options cache is empty or value format differs. */
export const ensureSelectedTripInSelectOptions = (
  options: TripSelectOption[],
  trip: Pick<JourneyDayTripItem, "day_trip_name">,
  selectedValue: string,
  fallbackLabel?: string,
): TripSelectOption[] => {
  if (!selectedValue) return options
  if (options.some((opt) => String(opt.value) === String(selectedValue))) return options
  const label =
    toTitleCase(String(trip.day_trip_name || fallbackLabel || "")).trim() || "Selected trip"
  return [...options, { label, value: selectedValue }]
}

const sameTripOption = (left: TripOptionItem, right: TripOptionItem): boolean =>
  String(left.day_trip_id) === String(right.day_trip_id) &&
  resolveTripSourceCityIdString(left) === resolveTripSourceCityIdString(right)

const mergeTripOptionLists = (
  incoming: TripOptionItem[] | undefined,
  selected: TripOptionItem[] | undefined,
): TripOptionItem[] => {
  const out = [...(incoming || [])]
  ;(selected || []).forEach((item) => {
    if (!item?.day_trip_id) return
    if (!out.some((existing) => sameTripOption(existing, item))) out.push(item)
  })
  return out
}

/** Catalog fetch replaces the cache; keep saved trips so the Select value still resolves. */
export const mergeJourneyOptionsWithSelected = (
  incoming: JourneyOptionsByDateValue,
  selected?: JourneyOptionsByDateValue,
): JourneyOptionsByDateValue => ({
  tours: mergeTripOptionLists(incoming.tours, selected?.tours),
  transfers: mergeTripOptionLists(incoming.transfers, selected?.transfers),
  excursions: (incoming.excursions || []).length > 0 ? incoming.excursions : selected?.excursions || [],
})

export const buildSelectedTripFallbackOptions = (day: JourneyDayItem): JourneyOptionsByDateValue => {
  const selectedTours: TripOptionItem[] = []
  const selectedTransfers: TripOptionItem[] = []

  ;(day.trips || []).forEach((trip: JourneyDayTripItem) => {
    if (!trip?.day_trip_id) return
    const sourceCityId = resolveTripSourceCityIdString(trip, day.city_id)
    const fallbackItem = {
      day_trip_id: trip.day_trip_id,
      day_trip_name: toTitleCase(trip.day_trip_name) || "",
      city_id: sourceCityId ? Number(sourceCityId) || day.city_id : day.city_id,
      city_name: String((trip as any)._source_city_name || trip.city_name || day.city_name || ""),
      _source_city_id: sourceCityId || undefined,
      _source_city_name: String((trip as any)._source_city_name || "") || undefined,
    } as TripOptionItem

    const kind = tripKindFromDayTripType(trip) || String((trip as any)._trip_kind || "").toLowerCase()
    if (kind === "tour") {
      selectedTours.push(fallbackItem)
      return
    }
    if (kind === "transfer") {
      selectedTransfers.push(fallbackItem)
      return
    }
    selectedTours.push(fallbackItem)
    selectedTransfers.push(fallbackItem)
  })

  return {
    tours: selectedTours,
    transfers: selectedTransfers,
    excursions: [],
  }
}
